// @ts-nocheck
import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Checkbox,
  Button,
  Stack,
  Divider,
  Card,
  CardActionArea,
  styled,
  ListItemIcon,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useVirtualizer } from '@tanstack/react-virtual';

import { ReactComponent as ReorderIcon } from '../../../../assets/Icons/ReorderIcon.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/Icons/DeleteIcon.svg';
import {
  EMPTY_ARRAY,
  RESTRICTED_DATA_TYPES_FOR_CONFIG_COLUMN,
} from '../../../../const/CommonConst';
import { getColumnName } from '../../../../utils/Utils';

const ItemTypes = {
  COLUMN: 'column',
};

const ConfigColumnListItem = React.memo(
  ({
    colName,
    label,
    isActive,
    onSelect,
    onRemove,
    theme,
    index,
    moveColumn,
  }) => {
    const ref = useRef(null);
    const dragRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    const [{ handlerId }, drop] = useDrop({
      accept: ItemTypes.COLUMN,
      collect(monitor) {
        return {
          handlerId: monitor.getHandlerId(),
        };
      },
      hover(item, monitor) {
        if (!ref.current) return;
        const dragIndex = item?.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;

        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        if (moveColumn) {
          moveColumn(dragIndex, hoverIndex);
        }
        // eslint-disable-next-line no-param-reassign
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag, preview] = useDrag({
      type: ItemTypes.COLUMN,
      item: () => ({ id: colName, index }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drop(ref);
    drag(dragRef);
    preview(ref);

    const handleMouseEnter = useCallback(() => {
      setIsHovered(true);
    }, [setIsHovered]);

    const handleMouseLeave = useCallback(() => {
      setIsHovered(false);
    }, [setIsHovered]);

    const handleDragHandleClick = useCallback((e) => {
      e.stopPropagation();
    }, []);

    const handleSelectClick = useCallback(() => {
      if (typeof onSelect === 'function') {
        const fn = onSelect(colName);
        if (typeof fn === 'function') fn();
      }
    }, [onSelect, colName]);

    const handleRemoveClick = useCallback(
      (e) => {
        if (typeof onRemove === 'function') {
          const fn = onRemove(colName);
          if (typeof fn === 'function') fn(e);
        }
      },
      [onRemove, colName]
    );

    return (
      <Card
        ref={ref}
        data-handler-id={handlerId}
        elevation={0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          mb: 1,
          opacity: isDragging ? 0.4 : 1,
          borderRadius: '4px',
          border: `1px solid ${isActive ? theme?.palette?.primary?.main : alpha(theme?.palette?.divider, 0.7)}`,
          bgcolor: isActive
            ? alpha(theme?.palette?.primary?.main, 0.2)
            : 'transparent',
        }}
      >
        <CardActionArea
          onClick={handleSelectClick}
          sx={{
            py: 1,
            px: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            '&:hover': {
              bgcolor: alpha(theme?.palette?.primary?.main, 0.04),
            },
          }}
          disableRipple
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box
              ref={dragRef}
              sx={{
                display: 'flex',
                alignItems: 'center',
                opacity: isHovered || isDragging ? 1 : 0,
                visibility: isHovered || isDragging ? 'visible' : 'hidden',
                transition: 'opacity 0.2s',
                color: alpha(theme.palette.text.secondary, 0.5),
                '&:hover': { color: theme.palette.text.primary },
                cursor: 'grab',
              }}
              onClick={handleDragHandleClick}
            >
              <ReorderIcon width={16} height={16} />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: isActive ? 'primary.main' : 'text.primary',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
          </Box>
          {isHovered ? (
            <IconButton
              title="Remove column"
              size="small"
              onClick={handleRemoveClick}
              sx={{
                color: theme?.palette?.advanceFilters?.defaultTextColor,
                width: 24,
                height: 24,
              }}
            >
              <DeleteIcon width={16} height={16} />
            </IconButton>
          ) : (
            <Box sx={{ width: 24, height: 24, flexShrink: 0 }} />
          )}
        </CardActionArea>
      </Card>
    );
  }
);

ConfigColumnListItem.propTypes = {
  colName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({
        main: PropTypes.string,
      }),
      divider: PropTypes.string,
      text: PropTypes.shape({
        secondary: PropTypes.string,
        primary: PropTypes.string,
      }),
      error: PropTypes.shape({
        main: PropTypes.string,
      }),
      advanceFilters: PropTypes.shape({
        defaultTextColor: PropTypes.string,
      }),
    }),
  }).isRequired,
  index: PropTypes.number.isRequired,
  moveColumn: PropTypes.func,
};

const AttributeListItem = styled(ListItem, {
  name: 'AttributeList',
  slot: 'listItem',
})();

const AttributeCheckboxItem = React.memo(({ attr, isChecked, onToggle }) => {
  const strId = String(attr?.id);

  const handleToggle = useCallback(() => {
    onToggle(strId);
  }, [onToggle, strId]);

  const handleCheckboxClick = useCallback(
    (e) => {
      e.stopPropagation();
      onToggle(strId);
    },
    [onToggle, strId]
  );

  return (
    <AttributeListItem
      sx={{
        width: '100% !important',
        maxHeight: '50px',
        mb: '8px',
        cursor: 'pointer',
      }}
      onClick={handleToggle}
    >
      <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
        <Checkbox
          size="small"
          checked={isChecked}
          onClick={handleCheckboxClick}
          tabIndex={-1}
          disableRipple
          sx={{ p: 0.5 }}
        />
      </ListItemIcon>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        overflow="hidden"
      >
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            maxWidth: '180px',
          }}
          title={`${attr?.label} : (${attr?.id}): (${attr?.name})`}
          variant="subtitle1"
        >
          {attr?.label || attr?.name}
        </Typography>
      </Box>
    </AttributeListItem>
  );
});

AttributeCheckboxItem.propTypes = {
  attr: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    name: PropTypes.string,
    attribute_origin: PropTypes.string,
  }).isRequired,
  isChecked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/**
 * Left sidebar for configColumn mode.
 * - Shows a list of selected attribute columns
 * - + button toggles a searchable attribute dropdown
 * - Hover on column row shows a delete icon
 * - Empty state when no columns are defined
 */
const ConfigColumnSidebar = ({
  schema,
  attributeList = EMPTY_ARRAY,
  selectedColumnName,
  onSelectColumn,
  onAddAttribute,
  onRemoveColumn,
  onReorderColumn,
}) => {
  const theme = useTheme();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [checkedAttributesIds, setCheckedAttributesIds] = useState([]);

  // Build ordered list of configured columns from schema
  const configuredColumns = useMemo(() => {
    const result = [];
    for (let i = 1; i <= (schema?.columns || 0); i += 1) {
      const colName = getColumnName(i);
      const settings = schema?.columnSettings?.[colName];
      if (!settings) continue;
      const config = settings?.attribute_config;
      const fullAttr = attributeList?.find(
        (attribute) => String(attribute?.id) === String(config?.attribute_id)
      );
      const apiSchema = fullAttr || config?.attribute_schema || {};
      const label = apiSchema?.label || config?.attribute_label || colName;
      const dataType = apiSchema?.data_type || config?._local_data_type || '';
      result.push({
        colName,
        label,
        dataType,
        attributeId: config?.attribute_id,
      });
    }
    return result;
  }, [schema, attributeList]);

  // IDs of attributes already selected as columns
  const selectedAttributeIds = useMemo(
    () =>
      new Set(
        configuredColumns.map((c) => String(c.attributeId)).filter(Boolean)
      ),
    [configuredColumns]
  );

  // Filter attribute list: exclude restricted types, system generated, already selected, and apply search
  const filteredAttributes = useMemo(() => {
    return (attributeList || []).filter((attr) => {
      if (RESTRICTED_DATA_TYPES_FOR_CONFIG_COLUMN.includes(attr?.data_type)) {
        return false;
      }
      if (attr?.attribute_origin === 'common') {
        return false;
      }
      if (selectedAttributeIds.has(String(attr?.id))) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = (attr?.name || '').toLowerCase().includes(q);
        const labelMatch = (attr?.label || '').toLowerCase().includes(q);
        return nameMatch || labelMatch;
      }
      return true;
    });
  }, [attributeList, searchQuery, selectedAttributeIds]);

  const handleToggleSearch = useCallback(() => {
    setShowSearch((prev) => {
      if (!prev) {
        setSearchQuery('');
        setCheckedAttributesIds([]);
      }
      return !prev;
    });
  }, [setShowSearch, setSearchQuery, setCheckedAttributesIds]);

  const flatVirtualizedData = useMemo(() => {
    const list = [];
    if (filteredAttributes.length > 0) {
      filteredAttributes.forEach((attr) => list.push({ type: 'item', attr }));
    }
    return list;
  }, [filteredAttributes]);

  const scrollRef = useRef(null);

  const virtualizer = useVirtualizer({
    count: flatVirtualizedData.length,
    estimateSize: (index) => {
      const type = flatVirtualizedData[index].type;
      if (type === 'header') return 30;
      if (type === 'divider') return 16;
      return 42;
    },
    getScrollElement: () => scrollRef.current,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const handleSearchChange = useCallback(
    (e) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery]
  );

  const handleToggleCheckbox = useCallback(
    (id) => {
      const strId = String(id);
      setCheckedAttributesIds((prev) => {
        if (prev.includes(strId)) {
          return prev.filter((i) => i !== strId);
        }
        return [...prev, strId];
      });
    },
    [setCheckedAttributesIds]
  );

  const handleSubmitSelection = useCallback(() => {
    checkedAttributesIds.forEach((id) => {
      const attr = attributeList.find((a) => String(a?.id) === id);
      if (attr) {
        onAddAttribute(attr);
      }
    });
    setCheckedAttributesIds([]);
    setShowSearch(false);
    setSearchQuery('');
  }, [
    checkedAttributesIds,
    attributeList,
    onAddAttribute,
    setCheckedAttributesIds,
    setShowSearch,
    setSearchQuery,
  ]);

  const handleOpenSearchFromEmpty = useCallback(() => {
    setShowSearch(true);
    setCheckedAttributesIds([]);
  }, [setShowSearch, setCheckedAttributesIds]);

  const handleRemoveColumnClick = useCallback(
    (colName) => (e) => {
      e?.stopPropagation();
      onRemoveColumn?.(colName);
    },
    [onRemoveColumn]
  );

  const handleSelectColumnClick = useCallback(
    (colName) => () => {
      if (showSearch) {
        setShowSearch(false);
        setCheckedAttributesIds([]);
      }
      onSelectColumn?.(colName);
    },
    [onSelectColumn, showSearch, setShowSearch, setCheckedAttributesIds]
  );

  const columnCount = configuredColumns.length;

  return (
    <Box
      sx={{
        width: 300,
        minWidth: 300,
        maxWidth: 300,
        borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: alpha(theme.palette.background.paper, 1),
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="body3"
          sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
          }}
        >
          {showSearch ? 'Select Attributes' : `Columns (${columnCount})`}
        </Typography>
        <IconButton
          size="small"
          onClick={handleToggleSearch}
          title={showSearch ? 'Close search' : 'Add attribute column'}
          sx={{
            width: 26,
            height: 26,
            bgcolor: alpha(theme?.palette?.primary?.main, 0.2),
            color: theme?.palette?.primary?.main,
          }}
        >
          {showSearch ? (
            <CloseIcon sx={{ fontSize: '16px' }} />
          ) : (
            <AddIcon sx={{ fontSize: '16px' }} />
          )}
        </IconButton>
      </Box>

      {/* ── Search / Attribute Multi-Select Dropdown ── */}
      {showSearch && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {/* Search input */}
          <Box sx={{ px: 2, py: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search library..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: '12px',
                  '& input': { py: '6px' },
                },
              }}
            />
          </Box>

          {/* Scrollable attribute list */}
          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 1,
              pb: 2,
            }}
          >
            {filteredAttributes.length === 0 ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  py: 4,
                  color: 'text.disabled',
                }}
              >
                No attributes found.
              </Typography>
            ) : (
              <List
                dense
                disablePadding
                sx={{ height: `${totalSize}px`, position: 'relative' }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
                  }}
                >
                  {virtualItems.map(({ key, index }) => {
                    const rowData = flatVirtualizedData[index];

                    if (rowData.type === 'header') {
                      return (
                        <Box
                          key={key}
                          data-index={index}
                          ref={virtualizer.measureElement}
                        >
                          <Typography
                            variant="body3"
                            sx={{
                              px: 1,
                              py: 0.5,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              display: 'block',
                            }}
                          >
                            {rowData.title}
                          </Typography>
                        </Box>
                      );
                    }
                    if (rowData.type === 'divider') {
                      return (
                        <Box
                          key={key}
                          data-index={index}
                          ref={virtualizer.measureElement}
                        >
                          <Divider sx={{ my: 1 }} />
                        </Box>
                      );
                    }
                    if (rowData.type === 'item') {
                      const attr = rowData.attr;
                      const strId = String(attr?.id);
                      const isChecked = checkedAttributesIds.includes(strId);
                      return (
                        <Box
                          key={key}
                          data-index={index}
                          ref={virtualizer.measureElement}
                        >
                          <AttributeCheckboxItem
                            attr={attr}
                            isChecked={isChecked}
                            onToggle={handleToggleCheckbox}
                          />
                        </Box>
                      );
                    }
                    return null;
                  })}
                </Box>
              </List>
            )}
          </Box>

          <Divider />
          {/* Submit Actions */}

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={1}
            mx={2}
            my={1}
          >
            <Button
              color="primary"
              variant="outlined"
              onClick={handleToggleSearch}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleSubmitSelection}
              disabled={checkedAttributesIds.length === 0}
            >
              Add{' '}
              {checkedAttributesIds.length > 0 &&
                `(${checkedAttributesIds.length})`}
            </Button>
          </Stack>
        </Box>
      )}

      {/* ── Column List / Empty State — hidden while search is open ── */}
      {!showSearch && (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {columnCount === 0 ? (
            /* Empty state */
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 1,
                p: 2,
                color: 'text.disabled',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TuneIcon
                  sx={{
                    fontSize: 22,
                    color: alpha(theme.palette.text.disabled, 0.7),
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textAlign: 'center',
                  fontSize: '12px',
                }}
              >
                No columns defined yet.
              </Typography>
              <Button
                variant="text"
                color="primary"
                onClick={handleOpenSearchFromEmpty}
              >
                Select from library
              </Button>
            </Box>
          ) : (
            /* Column list */
            <DndProvider backend={HTML5Backend}>
              <Box
                sx={{
                  px: 1,
                  pb: 2,
                  pt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {configuredColumns.map(({ colName, label }, index) => {
                  const isActive = selectedColumnName === colName;
                  return (
                    <ConfigColumnListItem
                      key={colName}
                      colName={colName}
                      label={label}
                      isActive={isActive}
                      onSelect={handleSelectColumnClick}
                      onRemove={handleRemoveColumnClick}
                      theme={theme}
                      index={index}
                      moveColumn={onReorderColumn}
                    />
                  );
                })}
              </Box>
            </DndProvider>
          )}
        </Box>
      )}
    </Box>
  );
};

ConfigColumnSidebar.propTypes = {
  schema: PropTypes.shape({
    columns: PropTypes.number,
    columnSettings: PropTypes.shape({}),
  }),
  attributeList: PropTypes.arrayOf(PropTypes.shape({})),
  selectedColumnName: PropTypes.string,
  onSelectColumn: PropTypes.func.isRequired,
  onAddAttribute: PropTypes.func.isRequired,
  onRemoveColumn: PropTypes.func.isRequired,
  onReorderColumn: PropTypes.func,
};

export default ConfigColumnSidebar;
